import {
	Card,
	Grid,
	Group,
	Skeleton,
	Stack,
	Text,
	ThemeIcon,
	Title,
} from "@mantine/core";
import { Receipt } from "lucide-react";
import type { KeuanganReport } from "@/api/transforms/keuangan-apbdes";
import { useIsDark } from "@/hooks/useIsDark";
import { useTranslate } from "@/hooks/useTranslate";
import { formatM } from "./format";
import { laporanTotals } from "./laporan-totals";

interface LaporanCardProps {
	report: KeuanganReport | null;
	loading: boolean;
}

export function LaporanCard({ report, loading }: LaporanCardProps) {
	const t = useTranslate();
	const dark = useIsDark();

	const empty =
		!report || (report.income.length === 0 && report.expenses.length === 0);

	const incomeRows = report?.income ?? [];
	const expenseRows = report?.expenses ?? [];
	const totals = laporanTotals(report);
	const rupiah = (v: number) => `${v < 0 ? "-" : ""}Rp ${formatM(Math.abs(v))}`;
	const realLine = (real: number, percent: number) =>
		`${t.keuanganAnggaran.realisasiLaporan}: ${rupiah(real)} (${percent}%)`;
	const saldoColor = (v: number) =>
		v >= 0 ? "var(--app-success)" : "var(--app-danger)";

	return (
		<Card
			data-ai-target="keuangan.laporan"
			p="md"
			radius="xl"
			withBorder
			style={{
				backgroundColor: "var(--app-card)",
				borderColor: "var(--app-border)",
				boxShadow: "var(--mantine-shadow-xs)",
			}}
			h="100%"
		>
			<Group gap="xs" mb="md">
				<ThemeIcon
					color="darmasaba-navy.7"
					variant="filled"
					size="sm"
					radius="sm"
				>
					<Receipt size={14} />
				</ThemeIcon>
				<Title order={4} c={dark ? "white" : "gray.9"}>
					{t.keuanganAnggaran.laporanApbdes}
				</Title>
			</Group>

			{loading ? (
				<Skeleton height={280} radius="md" />
			) : empty ? (
				<Group justify="center" align="center" h={280}>
					<Text size="sm" c="dimmed">
						Belum ada data laporan APBDes.
					</Text>
				</Group>
			) : (
				<>
					<Grid gutter="md">
						<Grid.Col span={{ base: 12, sm: 6 }} style={{ display: "flex" }}>
							<Card
								p="sm"
								radius="lg"
								withBorder
								style={{
									backgroundColor: "var(--app-card-alt)",
									borderColor: "var(--app-border)",
									display: "flex",
									flex: 1,
									flexDirection: "column",
								}}
							>
								<Title
									order={5}
									style={{ color: "var(--app-success)" }}
									mb="sm"
								>
									{t.keuanganAnggaran.pendapatan}
								</Title>
								<Stack gap="xs" style={{ flex: 1 }}>
									{incomeRows.map((item) => (
										<Group
											key={item.category}
											justify="space-between"
											align="flex-start"
											wrap="nowrap"
										>
											<Text
												size="sm"
												c={dark ? "gray.3" : "gray.7"}
												style={{ flex: 1, minWidth: 0 }}
											>
												{item.category}
											</Text>
											<Text
												size="sm"
												fw={600}
												style={{
													color: "var(--app-success)",
													flexShrink: 0,
													textAlign: "right",
													whiteSpace: "nowrap",
												}}
											>
												Rp {formatM(item.amount)}
											</Text>
										</Group>
									))}
								</Stack>
								<Group
									justify="space-between"
									mt="sm"
									pt="sm"
									style={{ borderTop: "1px solid var(--app-success)" }}
								>
									<Text fw={700} style={{ color: "var(--app-success)" }}>
										{t.keuanganAnggaran.totalAnggaran}
									</Text>
									<Text fw={700} style={{ color: "var(--app-success)" }}>
										{rupiah(totals.totalIncomeBudget)}
									</Text>
								</Group>
								<Text size="xs" c="dimmed" ta="right" data-report-real="income">
									{realLine(totals.totalIncomeReal, totals.incomePercent)}
								</Text>
							</Card>
						</Grid.Col>

						<Grid.Col span={{ base: 12, sm: 6 }} style={{ display: "flex" }}>
							<Card
								p="sm"
								radius="lg"
								withBorder
								style={{
									backgroundColor: "var(--app-card-alt)",
									borderColor: "var(--app-border)",
									display: "flex",
									flex: 1,
									flexDirection: "column",
								}}
							>
								<Title order={5} style={{ color: "var(--app-danger)" }} mb="sm">
									{t.keuanganAnggaran.belanja}
								</Title>
								<Stack gap="xs" style={{ flex: 1 }}>
									{expenseRows.map((item) => (
										<Group
											key={item.category}
											justify="space-between"
											align="flex-start"
											wrap="nowrap"
										>
											<Text
												size="sm"
												c={dark ? "gray.3" : "gray.7"}
												style={{ flex: 1, minWidth: 0 }}
											>
												{item.category}
											</Text>
											<Text
												size="sm"
												fw={600}
												style={{
													color: "var(--app-danger)",
													flexShrink: 0,
													textAlign: "right",
													whiteSpace: "nowrap",
												}}
											>
												Rp {formatM(item.amount)}
											</Text>
										</Group>
									))}
								</Stack>
								<Group
									justify="space-between"
									mt="sm"
									pt="sm"
									style={{ borderTop: "1px solid var(--app-danger)" }}
								>
									<Text fw={700} style={{ color: "var(--app-danger)" }}>
										{t.keuanganAnggaran.totalAnggaran}
									</Text>
									<Text fw={700} style={{ color: "var(--app-danger)" }}>
										{rupiah(totals.totalExpenseBudget)}
									</Text>
								</Group>
								<Text
									size="xs"
									c="dimmed"
									ta="right"
									data-report-real="expense"
								>
									{realLine(totals.totalExpenseReal, totals.expensePercent)}
								</Text>
							</Card>
						</Grid.Col>
					</Grid>

					<Stack
						gap={4}
						mt="md"
						pt="md"
						style={{
							borderTop: `1px solid var(--mantine-color-${dark ? "dark-4" : "gray-2"})`,
						}}
					>
						<Group justify="space-between">
							<Text fw={700} c={dark ? "white" : "gray.9"}>
								{t.keuanganAnggaran.saldoAnggaran}
							</Text>
							<Text
								fw={700}
								size="lg"
								data-report-saldo="budget"
								style={{ color: saldoColor(totals.saldoBudget) }}
							>
								{rupiah(totals.saldoBudget)}
							</Text>
						</Group>
						{totals.hasReal ? (
							<Group justify="space-between">
								<Text size="xs" c="dimmed">
									{t.keuanganAnggaran.saldoRealisasi}
								</Text>
								<Text
									size="sm"
									fw={600}
									data-report-saldo="real"
									style={{ color: saldoColor(totals.saldoReal) }}
								>
									{rupiah(totals.saldoReal)}
								</Text>
							</Group>
						) : null}
					</Stack>
				</>
			)}
		</Card>
	);
}
